import { z } from 'zod';
import { Ulid } from './common.js';
import { RateValue, wilson } from './game-events.js';
import { count, percent, timeWords } from './game-event-words.js';
import { AchievementCategory } from './play.js';
import { RARITY_MIN_PLAYERS, rarityPercent, rarityWord } from './rarity.js';
/**
 * What play services say back about achievements, to the two audiences that read them
 * (`docs/achievements-plan.md` §10, §12, phase A3): **anyone**, through the library's game modal
 * and the open read every community site and title screen may call, and **the team**, through the
 * channel's *Achievements* tab, the MCP server and the playground's `play_report`.
 *
 * Its own module rather than more of `play.ts`, which the catalog, the records and the layer's half
 * already fill, so the read side can grow — leaderboards' reads join it — without every phase
 * editing one file.
 */
/* --------------------------------------------------------------------------- anyone's read */
/**
 * How long a game's public achievements are cached, at the edge and in a browser (§9.7). Rarity
 * moves slowly, and the library opens a game far more often than anybody unlocks something in it.
 */
export const PUBLIC_ACHIEVEMENTS_MAX_AGE_SECONDS = 300;
/** An uploaded icon's two renditions, as anyone reads them; null for an achievement without one. */
export const PublicAchievementIcon = z.object({ '128': z.string(), '256': z.string() });
/**
 * One achievement as a stranger may read it: what a player is told, never the `trigger` — that
 * sentence is the team's and an agent's, and it names the code (§5.3). Hidden ones are not here at
 * all, only counted (`PublicAchievementsResponse.hidden`).
 */
export const PublicAchievement = z.object({
    /** The catalog's key: what a profile and a game's own trophy screen name it by. */
    key: z.string(),
    name: z.string(),
    description: z.string(),
    points: z.number().int(),
    /** A progress achievement's goal: *Build 100 towers* is reached at 100. */
    target: z.number().int().optional(),
    category: AchievementCategory.optional(),
    icon: PublicAchievementIcon.nullable(),
    /**
     * The share of the game's `public` players who hold it, or `null` while fewer than
     * `RARITY_MIN_PLAYERS` have played (§9.5). A surface shows nothing rather than a guess.
     */
    rarity: z.number().min(0).max(1).nullable(),
});
/**
 * `GET /v1/store/apps/:appId/achievements` — a library game's achievements, the same for everybody
 * (§10.2). In the catalog's order, which is the order the team's trophy room lists them.
 */
export const PublicAchievementsResponse = z.object({
    appId: Ulid,
    /** The catalog version this was read from. */
    version: z.number().int(),
    /** Every achievement's points together, hidden ones included: the game's total. */
    points: z.number().int(),
    achievements: z.array(PublicAchievement),
    /** Hidden achievements are counted, never described (§12.1). */
    hidden: z.object({ count: z.number().int(), points: z.number().int() }),
});
/**
 * The band word for a share, or null where a surface says nothing (§9.5) — and for an achievement
 * nobody holds, which is not *Ultra rare*: nobody has shown it can be done.
 */
export function rarityLabel(share) {
    return share === null || share === undefined || share === 0 ? null : rarityWord(share);
}
/** `Rare · 12% of players`, *Nobody has this yet*, or null where a surface says nothing. */
export function rarityLine(share) {
    if (share === null || share === undefined)
        return null;
    return share === 0 ? 'Nobody has this yet' : `${rarityWord(share)} · ${rarityPercent(share)} of players`;
}
/* -------------------------------------------------------------------------- the team's read */
/**
 * What each result on the health line means, said to the team (§8.2, §12.3) — one phrase each, so
 * a column of refusals reads as what to do about it. `limited` is a fact and never a reason.
 */
export const PLAY_RESULT_WORDS = {
    ok: 'Kept',
    unknown_id: 'Not in the catalog: the code fires a key boardflight.play.json does not declare',
    guest: 'From a guest seat: expected, and nothing is kept for a guest',
    ai: 'From an AI seat: the code should fire only for human seats',
    out_of_bounds: 'Outside a board’s bounds',
    not_progress: 'A step of progress for an achievement with no target',
    bad_variant: 'A variant the board does not declare',
    duplicate: 'Sent twice in one batch',
    invalid: 'Malformed: a build whose BoardFlight code is out of step',
    disabled: 'Sent while that service was off on this channel',
    limited: 'Over the limit',
};
/**
 * *Nobody has this yet* is said only once this many players have played: below it a game simply
 * has not been played enough for an empty count to mean anything. Above it, an achievement nobody
 * holds is the signal that its trigger is broken or its goal unreachable (§12.3).
 */
export const ACHIEVEMENT_NOBODY_AFTER = 50;
/** `GET /v1/apps/:appId/achievements/report` — what it may be asked (§10.2). */
export const AchievementsReportQuery = z.object({
    /** One channel; omitted, every channel of the game, each player counted once. */
    channel: Ulid.optional(),
    /** The cohort: players whose first record on the channel arrived on or after this UTC day. */
    from: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    /** …and on or before this one. */
    to: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
/**
 * One listed achievement's results. The definition comes first — the trigger included, because
 * reading it beside the numbers is how a broken one is found — then what the players did.
 */
export const AchievementReportRow = z.object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    trigger: z.string(),
    points: z.number().int(),
    hidden: z.boolean(),
    target: z.number().int().optional(),
    category: AchievementCategory.optional(),
    /** The 128 px rendition, or null for none. */
    icon: z.string().nullable(),
    /** Holders out of the cohort's players, with its 95% Wilson interval. */
    holders: RateValue,
    /**
     * The share players are shown — the `public` channel's, whatever channel this report reads — or
     * null below `RARITY_MIN_PLAYERS` there (§9.5).
     */
    rarity: z.number().min(0).max(1).nullable(),
    /** The median time from a holder's first record to the unlock, in ms; null with no holders. */
    medianToUnlockMs: z.number().int().nullable(),
    /** Nobody holds it although `ACHIEVEMENT_NOBODY_AFTER` players have played (§12.3). */
    nobodyYet: z.boolean(),
});
export const AchievementsReportResponse = z.object({
    appId: Ulid,
    channelId: Ulid.nullable(),
    from: z.string().nullable(),
    to: z.string().nullable(),
    /** The catalog version whose achievements are listed; null for a game with no catalog yet. */
    catalogVersion: z.number().int().nullable(),
    /** Players in the cohort: everybody whose first record fell in the window, on the channel. */
    players: z.number().int(),
    /** The current catalog's achievements, in its order. A retired one is not listed (§5.5). */
    achievements: z.array(AchievementReportRow),
});
/**
 * One report row from a definition and its counts — the one place the rate, the interval and the
 * *nobody yet* line are decided, so the route and every test agree.
 */
export function achievementReportRow(key, definition, counted, players, extra) {
    const holders = Math.min(counted?.holders ?? 0, players);
    return {
        key,
        name: definition.name,
        description: definition.description,
        trigger: definition.trigger,
        points: definition.points,
        hidden: definition.hidden === true,
        ...(definition.target !== undefined ? { target: definition.target } : {}),
        ...(definition.category !== undefined ? { category: definition.category } : {}),
        icon: extra.icon,
        holders: wilson(holders, players),
        rarity: extra.rarity,
        medianToUnlockMs: holders > 0 ? (counted?.medianToUnlockMs ?? null) : null,
        nobodyYet: holders === 0 && players >= ACHIEVEMENT_NOBODY_AFTER,
    };
}
/**
 * How many hold it, in words: `12% of 240 players`, below `RARITY_MIN_PLAYERS` the count alone —
 * `3 of 8 players so far` — because a share of eight people is not one worth printing.
 */
export function holdersWords(row) {
    const { k, n, rate } = row.holders;
    if (n === 0)
        return 'no players yet';
    if (n < RARITY_MIN_PLAYERS || rate === null)
        return `${k} of ${count(n, 'player')} so far`;
    return `${percent(rate)} of ${count(n, 'player')}`;
}
/**
 * A report as lines an agent reads (`play_report`, and what the MCP tool's caller is told to look
 * for): each achievement with its numbers and, where it is the thing to look at, its trigger.
 */
export function achievementsReportLines(report) {
    if (report.catalogVersion === null || report.achievements.length === 0) {
        return ['This game declares no achievements yet, so there is nothing to report.'];
    }
    const scope = report.channelId ? 'this channel' : 'every channel';
    const window = report.from || report.to ? ` whose first record arrived ${report.from ?? 'any time'} to ${report.to ?? 'today'}` : '';
    const lines = [`${count(report.players, 'player')} on ${scope}${window}; catalog version ${report.catalogVersion}.`];
    if (report.players < RARITY_MIN_PLAYERS) {
        lines.push(`Fewer than ${RARITY_MIN_PLAYERS} players: counts, not shares, and nothing here is a finding yet.`);
    }
    for (const row of report.achievements) {
        const facts = [holdersWords(row)];
        if (row.medianToUnlockMs !== null)
            facts.push(`typically ${timeWords(row.medianToUnlockMs)} after a player's first record`);
        if (rarityLabel(row.rarity))
            facts.push(`${rarityLabel(row.rarity)} in the library`);
        lines.push('');
        lines.push(`${row.key} — ${row.name}${row.hidden ? ' (hidden)' : ''}, ${row.points} points: ${facts.join('; ')}.`);
        if (row.nobodyYet) {
            lines.push(`  NOBODY HAS THIS after ${count(report.players, 'player')} — read its trigger against the code:`);
            lines.push(`  trigger: ${row.trigger}`);
        }
    }
    return lines;
}
//# sourceMappingURL=play-reads.js.map